import React, { useState, useEffect, useRef, useMemo } from "react";
import styled from "styled-components";
import {
  FiUsers,
  FiSettings,
  FiBarChart,
  FiUpload,
  FiMessageCircle,
  FiMic,
  FiImage,
  FiShield,
  FiActivity,
  FiLogOut,
  FiCamera,
  FiEdit2,
  FiTrash2,
  FiCheck,
  FiX,
  FiHome,
} from "react-icons/fi";
import { FaPaw } from "react-icons/fa";
import { useAuth } from "../../hooks/useAuth";
import { authAPI, uploadAPI } from "../../api";
import UserSettingsModal from "../UserDashboard/UserSettingsModal";
import UserManagementModal from "./UserManagementModal";
import { adminAPI } from "../../api";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";


interface Video {
  video_id: string;
  video_url: string;
  title: string;
  source: string;
  size_mb?: number;
  status?: string;
}

interface AdminUser {
  id: number;
  email: string;
  name: string;
  plan_type: "free" | "free-trial" | "basic" | "premium";
  created_at: string;
  usage: {
    text_messages_sent: number;
    voice_minutes_used: number;
    image_uploads: number;
  };
  subscription_status: string;
  avatar_url?: string;
  is_active?: boolean;
  avatar_file?: File;
  new_password?: string;
  user_type: "user" | "admin" | "super-admin";
}

interface Analytics {
  users: {
    total: number;
    byPlan: {
      free: number;
      basic: number;
      premium: number;
    };
  };
  messages: {
    total: number;
    byType: {
      text: number;
      voice: number;
      image: number;
    };
  };
  usage: {
    totalVoiceMinutes: number;
    totalImageUploads: number;
  };
  subscriptions: {
    active: number;
    canceled: number;
    past_due: number;
    unpaid: number;
  };
}

type DocItem = {
  id: string | number;
  file_name: string;
  file_type?: string;
  category?: string;
  author?: string;
  created_at?: string;
  size_bytes?: number;
};

type MediaItem = {
  id: number;
  file_name: string;
  file_type: string;
  created_at: string;
};

interface FeatureSettings {
  enableVoiceMessages: boolean;
  enableImageUploads: boolean;
  enableAdminPanel: boolean;
  enableTTS: boolean;
  enableVideoResponses: boolean;
}

type PlanLimit = {
  plan_type: "free" | "basic" | "premium";
  text_messages: number;
  voice_minutes: number;
  image_uploads: number;
  include_videos: boolean;
  video_limit: number;
};
const AccentBar = styled.div`
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, #ff9900 0%, #ffd700 100%);
  border-radius: 0 0 12px 12px;
`;

const AdminContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #232b3a 0%, #34495e 100%);
  padding: 2rem 1rem;
`;

const AdminContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: #f8f9fb;
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(44, 62, 80, 0.1);
  overflow: hidden;
  border: 1px solid #f3f4f6;
`;

const Header = styled.div`
  background: #fff;
  padding: 2.2rem 2rem 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const HeaderRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderMain = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const UserAvatar = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffffffff 0%, #ffffffff 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.2rem;
  font-weight: 700;
  border: 3px solid #fffbe6;
  box-shadow: 0 2px 12px rgba(255, 179, 71, 0.1);
`;

const AdminDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const AdminName = styled.div`
  font-weight: 800;
  font-size: 1.45rem;
  color: #232b3a;
`;

const AdminEmail = styled.div`
  color: #6b7280;
  font-size: 1rem;
  margin-top: 2px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.7rem;
`;

const IconActionButton = styled.button`
  background: none;
  color: #ff9900;
  border: 1.5px solid #ffedd5;
  padding: 0.5rem 0.7rem;
  border-radius: 10px;
  font-size: 1.15rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  box-shadow: none;
  transition: all 0.16s;
  &:hover {
    background: #fff7ed;
    color: #ff9900;
    border-color: #ffd700;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.35rem;
  font-weight: 800;
  color: #ff9900;
  margin-bottom: 2.2rem;
`;

const ActionButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const TabContainer = styled.div`
  display: flex;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 0.5rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
`;

const Tab = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 1rem 1.5rem;
  border: none;
  background: ${(props) => (props.active ? "white" : "transparent")};
  color: ${(props) => (props.active ? "#374151" : "#64748b")};
  font-weight: ${(props) => (props.active ? "600" : "500")};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${(props) =>
    props.active ? "0 4px 12px rgba(0, 0, 0, 0.1)" : "none"};

  &:hover {
    color: #374151;
    background: ${(props) =>
      props.active ? "white" : "rgba(255, 255, 255, 0.5)"};
  }
`;

const SearchInput = styled.input`
  padding: 1rem 1.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  margin-bottom: 1.5rem;
  width: 100%;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #9ca3af;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Th = styled.th`
  background: #e5e7eb;
  color: #374151;
  font-weight: 600;
  padding: 1rem;
  text-align: left;
  border-bottom: 2px solid #e2e8f0;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.9rem;
  color: #374151;
`;

const ClickableTr = styled.tr`
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f9fafb;
  }

  &:active {
    background-color: #f3f4f6;
  }
`;

const PlanBadge = styled.span<{ plan: string }>`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${({ plan }) =>
    plan === "premium" ? "#374151" : plan === "basic" ? "#6b7280" : "#9ca3af"};
  color: white;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${(props) => {
    switch (props.status) {
      case "active":
        return "linear-gradient(135deg, #10b981, #059669)";
      case "canceled":
        return "linear-gradient(135deg, #ef4444, #dc2626)";
      case "past_due":
        return "linear-gradient(135deg, #f59e0b, #d97706)";
      case "unpaid":
        return "linear-gradient(135deg, #ef4444, #dc2626)";
      default:
        return "linear-gradient(135deg, #6b7280, #374151)";
    }
  }};
  color: white;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
  font-size: 1.1rem;
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  color: #cbd5e1;
  margin-bottom: 1rem;
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 0.5rem;
`;

const EmptyStateText = styled.p`
  color: #64748b;
  font-size: 1rem;
`;

const AnalyticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const AnalyticsCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
`;

const AnalyticsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const AnalyticsIcon = styled.div<{ color?: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${(props) => props.color || "#FF9900"}15;
  color: ${(props) => props.color || "#FF9900"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
`;

const AnalyticsTitle = styled.h3`
  margin: 0;
  color: #374151;
  font-size: 0.9rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AnalyticsValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ff9900;
  margin-bottom: 0.5rem;
`;

const AnalyticsSubtext = styled.div`
  font-size: 0.9rem;
  color: #64748b;
`;

const SettingsSection = styled.div`
  margin-bottom: 2rem;
`;

const SettingsTitle = styled.h3`
  margin-bottom: 1rem;
  color: #374151;
  font-size: 1.25rem;
  font-weight: 600;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  margin-bottom: 1rem;
  background: #fff;
  transition: border-color 0.2s;

  &:hover {
    border-color: #ff9900;
  }
`;

const ToggleLabel = styled.div`
  display: flex;
  flex-direction: column;
`;

const ToggleTitle = styled.span`
  font-weight: 600;
  color: #374151;
  font-size: 1rem;
  margin-bottom: 0.25rem;
`;

const ToggleDescription = styled.span`
  color: #64748b;
  font-size: 0.9rem;
`;

const ToggleSwitch = styled.button<{ active: boolean }>`
  width: 60px;
  height: 32px;
  border-radius: 16px;
  border: none;
  background: ${(props) => (props.active ? "#FF9900" : "#cbd5e1")};
  position: relative;
  cursor: pointer;
  transition: background 0.2s;

  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: ${(props) => (props.active ? "30px" : "2px")};
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s;
  }
`;

const SaveButton = styled.button`
  background: linear-gradient(135deg, #ff9900, #ffd700);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-1px);
  }
`;

const MainContent = styled.div`
  padding: 2.5rem 2rem 2.5rem 2rem;
  background: transparent;
  color: #232b3a;
`;

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding: 1rem 0;
  border-top: 1px solid #e5e7eb;
`;

const PaginationInfo = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const PaginationButton = styled.button<{
  $active?: boolean;
  $disabled?: boolean;
}>`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${(props) => (props.$active ? "#FF9900" : "#d1d5db")};
  background: ${(props) => (props.$active ? "#FF9900" : "white")};
  color: ${(props) =>
    props.$active ? "white" : props.$disabled ? "#9ca3af" : "#374151"};
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;
  min-width: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${(props) => (props.$active ? "#e67e22" : "#f9fafb")};
    border-color: ${(props) => (props.$active ? "#e67e22" : "#9ca3af")};
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const AvatarArea = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
  border: 2px solid #ffedd5;
  border-radius: 50px;
  box-shadow: 0 1px 2px rgba(255, 179, 71, 0.06);
  padding: 0.5rem 1rem;
  background: #fffbe6;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  width: 100%;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 4px 24px rgba(44, 62, 80, 0.07);
  border: 1.5px solid #f3f4f6;
  padding: 2.5rem 2rem 2rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 2.2rem;
  min-height: 480px;
  @media (max-width: 900px) {
    padding: 1.5rem 1rem 1rem 1rem;
    min-height: unset;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 800;
  color: #ff9900;
  margin-bottom: 1.5rem;
  letter-spacing: -0.5px;
`;

const DangerCard = styled(Card)`
  border: 2px solid #d32f2f;
  box-shadow: 0 4px 24px rgba(211, 47, 47, 0.08);
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.2rem;
  & button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.7rem;
    border-radius: 16px !important;
    font-weight: 700;
    font-size: 1rem;
    background: linear-gradient(90deg, #ff9900 0%, #ffd700 100%) !important;
    color: #fff !important;
    border: none !important;
    box-shadow: 0 2px 8px rgba(255, 153, 0, 0.1) !important;
    transition: all 0.16s;
    &:hover {
      background: linear-gradient(90deg, #ffd700 0%, #ff9900 100%) !important;
      color: #fff !important;
      box-shadow: 0 4px 16px rgba(255, 153, 0, 0.18) !important;
    }
    @media (max-width: 600px) {
      width: 100% !important;
      min-width: 0;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }
  }
`;

const PillBadge = styled.span<{ color?: string }>`
  display: inline-block;
  padding: 0.35em 1.2em;
  border-radius: 999px;
  font-size: 0.95em;
  font-weight: 700;
  text-transform: uppercase;
  background: ${({ color }) => color || "#e5e7eb"};
  color: #232b3a;
  letter-spacing: 0.5px;
  margin-right: 0.2em;
`;

const StyledTable = styled(Table)`
  th {
    background: #f3f4f6;
    color: #232b3a;
    font-weight: 800;
    font-size: 1.05rem;
    text-transform: uppercase;
    padding: 18px 16px;
    border-bottom: 2px solid #e5e7eb;
    letter-spacing: 0.5px;
  }
  td {
    padding: 16px 16px;
    font-size: 1.05rem;
    color: #232b3a;
    vertical-align: middle;
    border-bottom: 1px solid #f3f4f6;
  }
  tr {
    background: #fff;
    transition: background 0.15s;
  }
  tr:hover {
    background: #f9fafb;
  }
`;

const ActionIcons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState("");
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [featureSettings, setFeatureSettings] = useState<FeatureSettings>({
    enableVoiceMessages: true,
    enableImageUploads: true,
    enableAdminPanel: true,
    enableTTS: false,
    enableVideoResponses: false,
  });
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [userModalLoading, setUserModalLoading] = useState(false);
  const { user: adminUser, signOut, refreshUser } = useAuth();

const navigate = useNavigate();

useEffect(() => {
  const savedTab = localStorage.getItem("admin_active_tab");
  const validTabs = ["users", "analytics", "settings", "admins", "knowledge"] as const;

  if (savedTab && validTabs.includes(savedTab as any)) {
    setActiveTab(savedTab as typeof validTabs[number]);
  }
}, []);

useEffect(() => {
  localStorage.setItem("admin_active_tab", activeTab);
}, [activeTab]);



useEffect(() => {
  localStorage.setItem("admin_active_tab", activeTab);
}, [activeTab]);


  console.log("analytics", analytics);
  /* ---------- Fetch Data ---------- */
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      if (!adminUser) {
        setLoading(false);
        return;
      }

      try {
        const usersRes = await adminAPI.getUsers({
          page: currentPage,
          limit: itemsPerPage,
          search: search || undefined,
        });
        setUsers((usersRes.users as AdminUser[]) || []);

        setPagination(usersRes.pagination || null);

        const analyticsRes = await adminAPI.getAnalytics();
        setAnalytics(analyticsRes.analytics);
      } catch (err: any) {
        setError(err.message || "Error fetching data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [adminUser, currentPage, itemsPerPage, search]);

  const handleUserClick = (user: AdminUser) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };
  const handleCloseUserModal = () => {
    setShowUserModal(false);
    setSelectedUser(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      // Only fetch if adminUser is present (authenticated)
      if (!adminUser) {
        setLoading(false);
        return;
      }

      try {
        // Fetch users using adminAPI with pagination and search parameters
        const usersRes = await adminAPI.getUsers({
          page: currentPage,
          limit: itemsPerPage,
          search: search || undefined,
        });
        setUsers(usersRes.users || []);
        setPagination(usersRes.pagination || null);

        // Fetch analytics
        const analyticsRes = await adminAPI.getAnalytics();
        setAnalytics(analyticsRes.analytics);

        // If you have a settings API, fetch here as well
        // const settingsRes = await adminAPI.getSettings();
        // setFeatureSettings(settingsRes.settings);
      } catch (err: any) {
        setError(err.message || "Error fetching data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [adminUser, currentPage, itemsPerPage, search]);

  const handleFeatureToggle = (feature: keyof FeatureSettings) => {
    setFeatureSettings((prev) => ({
      ...prev,
      [feature]: !prev[feature],
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      // Try custom JWT token first, then Firebase token for backward compatibility
      const customToken = localStorage.getItem("auth_token");
      const firebaseToken = localStorage.getItem("firebase_token");
      const token = customToken || firebaseToken;

      const res = await fetch(`${API_URL}/admin/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token || ""}`,
        },
        credentials: "include",
        body: JSON.stringify({ settings: featureSettings }),
      });
      if (!res.ok) throw new Error("Failed to save settings");
      // Show success message or handle response
    } catch (err: any) {
      setError(err.message || "Error saving settings");
    } finally {
      setSaving(false);
    }
  };

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleUserUpdate = async (updatedUser: Partial<AdminUser>) => {
    if (!selectedUser) return;

    setUserModalLoading(true);
    try {
      // Try custom JWT token first, then Firebase token for backward compatibility
      const customToken = localStorage.getItem("auth_token");
      const firebaseToken = localStorage.getItem("firebase_token");
      const token = customToken || firebaseToken;

      if (!token) throw new Error("No authentication token");

      const formData = new FormData();
      formData.append("name", updatedUser.name || "");
      formData.append("email", updatedUser.email || "");
      formData.append("plan_type", updatedUser.plan_type || "free");
      formData.append("is_active", (updatedUser.is_active ?? true).toString());

      if (updatedUser.new_password) {
        formData.append("new_password", updatedUser.new_password);
      }

      if (updatedUser.avatar_file) {
        formData.append("avatar", updatedUser.avatar_file);
      }

      const response = await fetch(
        `${API_URL}/admin/users/${selectedUser.id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update user");
      }

      // Refresh the users list
      const usersRes = await fetch(`${API_URL}/admin/users`, {
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.users || []);
      }
    } catch (err: any) {
      console.error("Error updating user:", err);
      throw err;
    } finally {
      setUserModalLoading(false);
    }
  };

  const renderUsersTab = () => (
    <>
      <SearchInput
        type="text"
        placeholder="Search users by name or email..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {loading ? (
        <EmptyState>Loading users...</EmptyState>
      ) : error ? (
        <EmptyState>{error}</EmptyState>
      ) : users.length === 0 ? (
        <EmptyState>No users found.</EmptyState>
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Plan</Th>
                <Th>Status</Th>
                <Th>Text Msgs</Th>
                <Th>VOICE MSGS</Th>
                <Th>Images</Th>
                <Th>Joined</Th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <ClickableTr
                  key={user.id}
                  onClick={() => handleUserClick(user)}
                >
                  <Td>{user.name}</Td>
                  <Td>{user.email}</Td>
                  <Td>
                    <PlanBadge plan={user.plan_type}>
                      {user.plan_type}
                    </PlanBadge>
                  </Td>
                  <Td>
                    <StatusBadge status={user.subscription_status}>
                      {user.subscription_status}
                    </StatusBadge>
                  </Td>
                  <Td>{user.usage?.text_messages_sent ?? 0}</Td>
                  <Td>{Math.round(user.usage?.voice_minutes_used || 0)}</Td>
                  <Td>{user.usage?.image_uploads ?? 0}</Td>
                  <Td>{new Date(user.created_at).toLocaleDateString()}</Td>
                </ClickableTr>
              ))}
            </tbody>
          </Table>

          {pagination && pagination.totalPages > 1 && (
            <PaginationContainer>
              <PaginationInfo>
                Showing{" "}
                {(pagination.currentPage - 1) * pagination.itemsPerPage + 1} to{" "}
                {Math.min(
                  pagination.currentPage * pagination.itemsPerPage,
                  pagination.totalItems
                )}{" "}
                of {pagination.totalItems} users
              </PaginationInfo>
              <PaginationControls>
                <PaginationButton
                  $disabled={!pagination.hasPrevPage}
                  disabled={!pagination.hasPrevPage}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Previous
                </PaginationButton>

                {Array.from(
                  { length: Math.min(5, pagination.totalPages) },
                  (_, i) => {
                    let pageNum: number;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <PaginationButton
                        key={pageNum}
                        $active={currentPage === pageNum}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </PaginationButton>
                    );
                  }
                )}

                <PaginationButton
                  $disabled={!pagination.hasNextPage}
                  disabled={!pagination.hasNextPage}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                </PaginationButton>
              </PaginationControls>
            </PaginationContainer>
          )}
        </>
      )}
    </>
  );

  const renderAnalyticsTab = () => (
    <div>
      {loading && <EmptyState>Loading analytics...</EmptyState>}
      {error && <EmptyState>{error}</EmptyState>}
      {!loading && !error && !analytics && (
        <EmptyState>No analytics data available.</EmptyState>
      )}
      {analytics && (
        <>
          <AnalyticsGrid>
            <AnalyticsCard>
              <AnalyticsHeader>
                <AnalyticsTitle>Total Users</AnalyticsTitle>
                <AnalyticsIcon color="#FF9900">
                  <FiUsers />
                </AnalyticsIcon>
              </AnalyticsHeader>
              <AnalyticsValue>{analytics?.users.total || 0}</AnalyticsValue>
              <AnalyticsSubtext>Active users on platform</AnalyticsSubtext>
            </AnalyticsCard>

            <AnalyticsCard>
              <AnalyticsHeader>
                <AnalyticsIcon>
                  <FiMessageCircle size={20} />
                </AnalyticsIcon>
              </AnalyticsHeader>
              <AnalyticsValue>{analytics?.messages.total || 0}</AnalyticsValue>
              <AnalyticsSubtext>AI conversations</AnalyticsSubtext>
            </AnalyticsCard>

            <AnalyticsCard>
              <AnalyticsHeader>
                <AnalyticsIcon>
                  <FiMic size={20} />
                </AnalyticsIcon>
              </AnalyticsHeader>
              <AnalyticsValue>
                {Math.round(analytics?.usage.totalVoiceMinutes || 0)}
              </AnalyticsValue>
              <AnalyticsSubtext>Voice interactions Minutes </AnalyticsSubtext>
            </AnalyticsCard>

            <AnalyticsCard>
              <AnalyticsHeader>
                <AnalyticsIcon>
                  <FiImage size={20} />
                </AnalyticsIcon>
              </AnalyticsHeader>
              <AnalyticsValue>
                {analytics?.messages.byType.image || 0}
              </AnalyticsValue>
              <AnalyticsSubtext>Photo analysis</AnalyticsSubtext>
            </AnalyticsCard>

            <AnalyticsCard>
              <AnalyticsHeader>
                <AnalyticsIcon>
                  <FiShield size={20} />
                </AnalyticsIcon>
              </AnalyticsHeader>
              <AnalyticsValue>
                {analytics?.subscriptions.active || 0}
              </AnalyticsValue>
              <AnalyticsSubtext>Paid users</AnalyticsSubtext>
            </AnalyticsCard>

            <AnalyticsCard>
              <AnalyticsHeader>
                <AnalyticsTitle>Platform Activity</AnalyticsTitle>
                <AnalyticsIcon color="#6366f1">
                  <FiActivity />
                </AnalyticsIcon>
              </AnalyticsHeader>
              <AnalyticsValue>98%</AnalyticsValue>
              <AnalyticsSubtext>Uptime this month</AnalyticsSubtext>
            </AnalyticsCard>
          </AnalyticsGrid>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
              gap: "1.5rem",
            }}
          >
            <AnalyticsCard>
              <AnalyticsTitle style={{ marginBottom: "1rem" }}>
                Users by Plan
              </AnalyticsTitle>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#6b7280" }}>Free</span>
                  <span style={{ fontWeight: "600", color: "#374151" }}>
                    {analytics?.users.byPlan.free || 0}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#6b7280" }}>Basic</span>
                  <span style={{ fontWeight: "600", color: "#374151" }}>
                    {analytics?.users.byPlan.basic || 0}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#6b7280" }}>Premium</span>
                  <span style={{ fontWeight: "600", color: "#374151" }}>
                    {analytics?.users.byPlan.premium || 0}
                  </span>
                </div>
              </div>
            </AnalyticsCard>

            <AnalyticsCard>
              <AnalyticsTitle style={{ marginBottom: "1rem" }}>
                Messages by Type
              </AnalyticsTitle>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#6b7280" }}>Text</span>
                  <span style={{ fontWeight: "600", color: "#374151" }}>
                    {analytics?.messages.byType.text || 0}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#6b7280" }}>Voice</span>
                  <span style={{ fontWeight: "600", color: "#374151" }}>
                    {analytics?.messages.byType.voice || 0}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#6b7280" }}>Image</span>
                  <span style={{ fontWeight: "600", color: "#374151" }}>
                    {analytics?.messages.byType.image || 0}
                  </span>
                </div>
              </div>
            </AnalyticsCard>
          </div>
        </>
      )}
    </div>
  );

  const handleCloseSettings = async () => {
    setActiveTab("users");
    await refreshUser();
  };

  // Profile Section Component
  const ProfileSection = () => {
    const [name, setName] = useState(adminUser?.name || "");
    const [avatar, setAvatar] = useState(adminUser?.avatar_url || "");
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);

    // Separate state for name updates
    const [nameSuccess, setNameSuccess] = useState("");
    const [nameError, setNameError] = useState("");

    // Separate state for avatar updates
    const [avatarSuccess, setAvatarSuccess] = useState("");
    const [avatarError, setAvatarError] = useState("");

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Update local state when adminUser changes, but preserve success/error messages
    useEffect(() => {
      if (adminUser) {
        setName(adminUser.name || "");
        setAvatar(adminUser.avatar_url || "");
      }
    }, [adminUser?.name, adminUser?.avatar_url]);

    const handleNameSave = async (e: React.FormEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!name.trim()) {
        setNameError("Name cannot be empty");
        return;
      }

      setSaving(true);
      setNameSuccess("");
      setNameError("");

      try {
        await authAPI.updateProfile({ name: name.trim() });
        setNameSuccess("Name updated successfully!");

        // Clear success message after 5 seconds
        setTimeout(() => {
          setNameSuccess("");
          // Refresh user after success message is cleared
          refreshUser();
        }, 5000);
      } catch (err: any) {
        setNameError(err.message || "Failed to update name");

        // Clear error message after 5 seconds
        setTimeout(() => {
          setNameError("");
        }, 5000);
      } finally {
        setSaving(false);
      }
    };

    const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setAvatarFile(file);
        setAvatar(URL.createObjectURL(file));
      }
    };

    const handleAvatarSave = async () => {
      if (!avatarFile || !adminUser) return;

      setSaving(true);
      setAvatarSuccess("");
      setAvatarError("");

      try {
        const uploadRes = await uploadAPI.uploadImage(
          avatarFile,
          "User avatar"
        );
        const imageUrl = uploadRes?.upload?.url;
        if (!imageUrl) throw new Error("Upload failed");

        await authAPI.updateProfile({ avatar_url: imageUrl });
        setAvatarSuccess("Profile picture updated successfully!");
        setAvatarFile(null);

        // Clear success message after 5 seconds
        setTimeout(() => {
          setAvatarSuccess("");
          // Refresh user after success message is cleared
          refreshUser();
        }, 5000);
      } catch (err: any) {
        setAvatarError("Failed to update profile picture");

        // Clear error message after 5 seconds
        setTimeout(() => {
          setAvatarError("");
        }, 5000);
      } finally {
        setSaving(false);
      }
    };

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        {/* Profile Picture Section */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            padding: "20px",
            background: "#f8f9fa",
            borderRadius: "12px",
            border: "1px solid #e9ecef",
          }}
        >
          <div style={{ position: "relative" }}>
            {avatar || adminUser?.avatar_url ? (
              <img
                src={avatar || adminUser?.avatar_url}
                alt="Profile"
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "3px solid #E89B3C",
                  boxShadow: "0 4px 16px rgba(232, 155, 60, 0.2)",
                }}
                onError={(e) => {
                  // Fallback if image fails to load
                  e.currentTarget.style.display = "none";
                  e.currentTarget.nextElementSibling?.setAttribute(
                    "style",
                    "display: flex"
                  );
                }}
              />
            ) : (
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #ffffffff 0%, #ffffffff 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: "32px",
                  border: "3px solid #E89B3C",
                }}
              >
                                  <FaPaw className="text-orange" size={34} />

              </div>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{
                position: "absolute",
                bottom: "0",
                right: "0",
                background: "#E89B3C",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: "28px",
                height: "28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#FF9900";
                e.currentTarget.style.transform = "scale(1.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#E89B3C";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <FiCamera size={14} />
            </button>
          </div>
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            ref={fileInputRef}
            onChange={handleAvatarChange}
          />
          {avatarFile && (
            <button
              type="button"
              onClick={handleAvatarSave}
              disabled={saving}
              style={{
                background: "linear-gradient(135deg, #E89B3C 0%, #FF9900 100%)",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.6 : 1,
                transition: "all 0.2s",
              }}
            >
              {saving ? "Saving..." : "Save Picture"}
            </button>
          )}

          {/* Success/Error Messages for Avatar */}
          {avatarSuccess && (
            <div
              style={{
                color: "#059669",
                fontSize: "14px",
                fontWeight: "600",
                backgroundColor: "#d1fae5",
                border: "1px solid #10b981",
                borderRadius: "6px",
                padding: "8px 12px",
                textAlign: "center",
              }}
            >
              ✅ {avatarSuccess}
            </div>
          )}
          {avatarError && (
            <div
              style={{
                color: "#dc2626",
                fontSize: "14px",
                fontWeight: "600",
                backgroundColor: "#fef2f2",
                border: "1px solid #f87171",
                borderRadius: "6px",
                padding: "8px 12px",
                textAlign: "center",
              }}
            >
              ❌ {avatarError}
            </div>
          )}
        </div>

        {/* Name Update Section */}
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <h4
            style={{
              margin: "0",
              fontSize: "16px",
              fontWeight: "600",
              color: "#374151",
            }}
          >
            Display Name
          </h4>
          <form
            onSubmit={handleNameSave}
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            noValidate
            onKeyDown={(e) => {
              if (e.key === "Enter" && (saving || !name.trim())) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={saving}
              required
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "15px",
                transition: "border-color 0.2s",
                background: "white",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#E89B3C")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              placeholder="Enter your display name"
            />
            {nameSuccess && (
              <div
                style={{
                  color: "#059669",
                  fontSize: "14px",
                  fontWeight: "600",
                  backgroundColor: "#d1fae5",
                  border: "1px solid #10b981",
                  borderRadius: "6px",
                  padding: "8px 12px",
                }}
              >
                ✅ {nameSuccess}
              </div>
            )}
            {nameError && (
              <div
                style={{
                  color: "#dc2626",
                  fontSize: "14px",
                  fontWeight: "600",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #f87171",
                  borderRadius: "6px",
                  padding: "8px 12px",
                }}
              >
                ❌ {nameError}
              </div>
            )}
            <button
              type="submit"
              disabled={saving || !name.trim()}
              style={{
                background: "linear-gradient(135deg, #E89B3C 0%, #FF9900 100%)",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "12px 20px",
                fontSize: "15px",
                fontWeight: "600",
                cursor: saving || !name.trim() ? "not-allowed" : "pointer",
                opacity: saving || !name.trim() ? 0.6 : 1,
                transition: "all 0.2s",
                alignSelf: "flex-start",
              }}
              onClick={(e) => {
                // Additional safety to prevent any default behavior
                if (saving || !name.trim()) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
              {saving ? "Saving..." : "Update Name"}
            </button>
          </form>
        </div>
      </div>
    );
  };

  // Security Section Component
  const SecuritySection = () => {
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [pwSuccess, setPwSuccess] = useState("");
    const [pwError, setPwError] = useState("");
    const [pwSaving, setPwSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState("");
    const [deleteError, setDeleteError] = useState("");
    const [deleteSuccess, setDeleteSuccess] = useState("");
    const [deleting, setDeleting] = useState(false);

    const handlePasswordSave = async () => {
      console.log("Password save function called");
      console.log("Old password length:", oldPassword.length);
      console.log("New password length:", newPassword.length);
      console.log("Confirm password length:", confirmPassword.length);

      setPwSuccess("");
      setPwError("");

      if (newPassword !== confirmPassword) {
        setPwError("New passwords do not match");
        return;
      }

      if (newPassword.length < 6) {
        setPwError("New password must be at least 6 characters long");
        return;
      }

      if (!oldPassword || oldPassword.length === 0) {
        setPwError("Current password is required");
        return;
      }

      console.log("Starting password update...");
      setPwSaving(true);

      try {
        console.log("Calling authAPI.changePassword...");
        const response = await authAPI.changePassword(oldPassword, newPassword);
        console.log("Password change response:", response);

        if (response.success) {
          console.log("Password updated successfully!");
          setPwSuccess("Password updated successfully!");
          setOldPassword("");
          setNewPassword("");
          setConfirmPassword("");

          // Clear success message after 5 seconds
          setTimeout(() => {
            setPwSuccess("");
          }, 5000);
        } else {
          console.log("Password update failed:", response.message);
          setPwError(response.message || "Failed to update password");

          // Clear error message after 5 seconds
          setTimeout(() => {
            setPwError("");
          }, 5000);
        }
      } catch (err: any) {
        console.log("Password update error:", err);
        console.log("Error response:", err.response?.data);

        // Handle specific error cases
        if (err.response?.status === 401) {
          setPwError("Current password is incorrect");
        } else if (err.response?.status === 400) {
          setPwError(err.response.data?.message || "Invalid request");
        } else {
          setPwError(err.message || "Failed to update password");
        }

        // Clear error message after 5 seconds
        setTimeout(() => {
          setPwError("");
        }, 5000);
      } finally {
        console.log("Password update process completed");
        setPwSaving(false);
      }
    };

const handleDeleteAccount = async (e: React.FormEvent) => {
  e.preventDefault();
  setDeleteError('');
  setDeleteSuccess('');
  setDeleting(true);

  if (deleteConfirm !== adminUser?.email) {
    setDeleteError('Please type your email to confirm.');
    setDeleting(false);
    return;
  }

  try {
    console.log('Deleting account...');
    const result = await authAPI.deleteAccount();
    console.log('Delete account result:', result);

    if (result.success) {
      setDeleteSuccess('Your account has been deleted.');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.clear();
      window.location.href = '/';
    } else {
      setDeleteError(result.message || 'Failed to delete account.');
    }
  } catch (err: any) {
    console.error('Delete account failed:', err);
    setDeleteError(
      err.response?.data?.message ||
      err.message ||
      'Failed to delete account.'
    );
  } finally {
    setDeleting(false);
  }
};

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
        {/* Password Change Section */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <h4
            style={{
              margin: "0",
              fontSize: "16px",
              fontWeight: "600",
              color: "#374151",
            }}
          >
            Change Password
          </h4>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <input
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              disabled={pwSaving}
              required
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "15px",
                transition: "border-color 0.2s",
                background: "white",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#E89B3C")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              placeholder="Current password"
            />
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={pwSaving}
              required
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "15px",
                transition: "border-color 0.2s",
                background: "white",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#E89B3C")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              placeholder="New password"
            />
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={pwSaving}
              required
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "15px",
                transition: "border-color 0.2s",
                background: "white",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#E89B3C")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              placeholder="Confirm new password"
            />
            {pwSuccess && (
              <div
                style={{
                  color: "#059669",
                  fontSize: "14px",
                  fontWeight: "600",
                  backgroundColor: "#d1fae5",
                  border: "1px solid #10b981",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  marginBottom: "8px",
                }}
              >
                ✅ {pwSuccess}
              </div>
            )}
            {pwError && (
              <div
                style={{
                  color: "#dc2626",
                  fontSize: "14px",
                  fontWeight: "600",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #f87171",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  marginBottom: "8px",
                }}
              >
                ❌ {pwError}
              </div>
            )}
            <button
              type="button"
              disabled={
                pwSaving || !oldPassword || !newPassword || !confirmPassword
              }
              style={{
                background: "linear-gradient(135deg, #E89B3C 0%, #FF9900 100%)",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "12px 20px",
                fontSize: "15px",
                fontWeight: "600",
                cursor:
                  pwSaving || !oldPassword || !newPassword || !confirmPassword
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  pwSaving || !oldPassword || !newPassword || !confirmPassword
                    ? 0.6
                    : 1,
                transition: "all 0.2s",
                alignSelf: "flex-start",
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (
                  !pwSaving &&
                  oldPassword &&
                  newPassword &&
                  confirmPassword
                ) {
                  handlePasswordSave();
                }
              }}
            >
              {pwSaving ? "Updating..." : "Update Password"}
            </button>
          </div>
        </div>

        {/* Delete Account Section */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            padding: "20px",
            background: "#fef2f2",
            borderRadius: "12px",
            border: "1px solid #fecaca",
          }}
        >
          <h4
            style={{
              margin: "0",
              fontSize: "16px",
              fontWeight: "600",
              color: "#dc2626",
            }}
          >
            Delete Account
          </h4>
          <p
            style={{
              margin: "0",
              fontSize: "14px",
              color: "#7f1d1d",
              lineHeight: "1.5",
            }}
          >
            This action cannot be undone. All your data will be permanently
            deleted.
          </p>
          <form
            onSubmit={handleDeleteAccount}
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <input
              type="text"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              disabled={deleting}
              required
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid #fecaca",
                borderRadius: "8px",
                fontSize: "15px",
                transition: "border-color 0.2s",
                background: "white",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#dc2626")}
              onBlur={(e) => (e.target.style.borderColor = "#fecaca")}
              placeholder={`Type "${adminUser?.email}" to confirm`}
            />
            {deleteSuccess && (
              <div
                style={{
                  color: "#059669",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {deleteSuccess}
              </div>
            )}
            {deleteError && (
              <div
                style={{
                  color: "#dc2626",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {deleteError}
              </div>
            )}
            <button
              type="submit"
              disabled={deleting || deleteConfirm !== adminUser?.email}
              style={{
                background: "#dc2626",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "12px 20px",
                fontSize: "15px",
                fontWeight: "600",
                cursor:
                  deleting || deleteConfirm !== adminUser?.email
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  deleting || deleteConfirm !== adminUser?.email ? 0.6 : 1,
                transition: "all 0.2s",
                alignSelf: "flex-start",
              }}
            >
              {deleting ? "Deleting..." : "Delete Account"}
            </button>
          </form>
        </div>
      </div>
    );
  };

  // PlanLimitsCard component
  const PlanLimitsCard = () => {
    const [planLimits, setPlanLimits] = useState<PlanLimit[]>([]);
    const [editRow, setEditRow] = useState<PlanLimit["plan_type"] | null>(null);
    const [editValues, setEditValues] = useState<Partial<PlanLimit>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

    useEffect(() => {
      fetchPlanLimits();
    }, []);

    const fetchPlanLimits = async () => {
      setLoading(true);
      setError(null);
      try {
        // Try custom JWT token first, then Firebase token for backward compatibility
        const customToken = localStorage.getItem("auth_token");
        const firebaseToken = localStorage.getItem("firebase_token");
        const token = customToken || firebaseToken;

        const res = await axios.get(`${API_URL}/admin/plan-limits`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPlanLimits(res.data.plan_limits);
      } catch (err) {
        setError("Failed to fetch plan limits");
      } finally {
        setLoading(false);
      }
    };

    const handleEdit = (planType: PlanLimit["plan_type"]) => {
      setEditRow(planType);
      const row = planLimits.find((p) => p.plan_type === planType);
      setEditValues(row ? { ...row } : {});
    };

    const handleCancel = () => {
      setEditRow(null);
      setEditValues({});
    };

    const handleChange = (
      field: keyof Omit<PlanLimit, "plan_type">,
      value: number | boolean
    ) => {
      setEditValues((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
      if (!editRow) return;
      setSaveLoading(true);
      setSaveError(null);
      setSaveSuccess(null);
      try {
        // Try custom JWT token first, then Firebase token for backward compatibility
        const customToken = localStorage.getItem("auth_token");
        const firebaseToken = localStorage.getItem("firebase_token");
        const token = customToken || firebaseToken;

        await axios.put(
          `${API_URL}/admin/plan-limits/${editRow}`,
          {
            text_messages: editValues.text_messages,
            voice_minutes: editValues.voice_minutes,
            image_uploads: editValues.image_uploads,
            include_videos: editValues.include_videos,
            video_limit: editValues.video_limit,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSaveSuccess("Plan limits updated!");
        setEditRow(null);
        setEditValues({});
        await fetchPlanLimits();
      } catch (err: any) {
        setSaveError(
          err.response?.data?.error || "Failed to update plan limits"
        );
      } finally {
        setSaveLoading(false);
        setTimeout(() => setSaveSuccess(null), 2000);
      }
    };

    // Helper: is built-in plan
    const isBuiltInPlan = (planType: string) =>
      ["free", "free-trial", "basic", "premium"].includes(planType);

    return (
      <Card>
        <CardTitle>Plan Limits</CardTitle>
        <CardContent>
          {loading ? (
            <div>Loading...</div>
          ) : error ? (
            <div style={{ color: "red" }}>{error}</div>
          ) : (
            <StyledTable>
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Text Messages</th>
                  <th>Voice Minutes</th>
                  <th>Image Uploads</th>
                  <th>Include Videos</th>
                  <th>Video Limit</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {planLimits.map((plan) => {
                  const editing = editRow === plan.plan_type;
                  return (
                    <tr
                      key={plan.plan_type}
                      style={
                        editing
                          ? {
                              background: "#fffbe6",
                              boxShadow: "0 2px 8px #ffe06633",
                            }
                          : {}
                      }
                    >
                      <td>
                        <PillBadge
                          color={
                            plan.plan_type === "premium"
                              ? "#232b3a"
                              : plan.plan_type === "basic"
                              ? "#6b7280"
                              : "#e5e7eb"
                          }
                          style={{
                            color:
                              plan.plan_type === "premium" ? "#fff" : "#232b3a",
                          }}
                        >
                          {plan.plan_type}
                        </PillBadge>
                      </td>
                      <td>
                        {editing ? (
                          <input
                            type="number"
                            value={editValues.text_messages}
                            onChange={(e) =>
                              handleChange(
                                "text_messages",
                                e.target.valueAsNumber
                              )
                            }
                            style={{
                              width: 80,
                              padding: "4px 8px",
                              borderRadius: 4,
                              border: "1px solid #e5e7eb",
                            }}
                          />
                        ) : plan.text_messages === -1 ? (
                          <PillBadge color="#e0e7ff">Unlimited</PillBadge>
                        ) : (
                          plan.text_messages
                        )}
                      </td>
                      <td>
                        {editing ? (
                          <input
                            type="number"
                            value={editValues.voice_minutes}
                            onChange={(e) =>
                              handleChange(
                                "voice_minutes",
                                e.target.valueAsNumber
                              )
                            }
                            style={{
                              width: 80,
                              padding: "4px 8px",
                              borderRadius: 4,
                              border: "1px solid #e5e7eb",
                            }}
                          />
                        ) : plan.voice_minutes === -1 ? (
                          <PillBadge color="#e0e7ff">Unlimited</PillBadge>
                        ) : (
                          plan.voice_minutes
                        )}
                      </td>
                      <td>
                        {editing ? (
                          <input
                            type="number"
                            value={editValues.image_uploads}
                            onChange={(e) =>
                              handleChange(
                                "image_uploads",
                                e.target.valueAsNumber
                              )
                            }
                            style={{
                              width: 80,
                              padding: "4px 8px",
                              borderRadius: 4,
                              border: "1px solid #e5e7eb",
                            }}
                          />
                        ) : plan.image_uploads === -1 ? (
                          <PillBadge color="#e0e7ff">Unlimited</PillBadge>
                        ) : (
                          plan.image_uploads
                        )}
                      </td>
                      <td>
                        {editing ? (
                          <input
                            type="checkbox"
                            checked={editValues.include_videos}
                            onChange={(e) =>
                              handleChange("include_videos", e.target.checked)
                            }
                            style={{
                              width: 20,
                              height: 20,
                              accentColor: "#FF9900",
                            }}
                          />
                        ) : (
                          <span>{plan.include_videos ? "Yes" : "No"}</span>
                        )}
                      </td>
                      <td>
                        {editing ? (
                          <input
                            type="number"
                            value={editValues.video_limit}
                            onChange={(e) =>
                              handleChange(
                                "video_limit",
                                e.target.valueAsNumber
                              )
                            }
                            style={{
                              width: 80,
                              padding: "4px 8px",
                              borderRadius: 4,
                              border: "1px solid #e5e7eb",
                            }}
                          />
                        ) : plan.video_limit === -1 ? (
                          <PillBadge color="#e0e7ff">Unlimited</PillBadge>
                        ) : (
                          plan.video_limit
                        )}
                      </td>
                      <td style={{ minWidth: 120 }}>
                        {editing ? (
                          <ActionIcons>
                            <IconButton
                              title="Save"
                              onClick={handleSave}
                              disabled={saveLoading}
                              style={{ color: "#059669" }}
                            >
                              <FiCheck size={18} />
                            </IconButton>
                            <IconButton
                              title="Cancel"
                              onClick={handleCancel}
                              style={{ color: "#ef4444" }}
                            >
                              <FiX size={18} />
                            </IconButton>
                          </ActionIcons>
                        ) : (
                          <ActionIcons>
                            <IconButton
                              title="Edit"
                              onClick={() => handleEdit(plan.plan_type)}
                              style={{ color: "#f59e0b" }}
                            >
                              <FiEdit2 size={18} />
                            </IconButton>
                            <IconButton
                              title={
                                isBuiltInPlan(plan.plan_type)
                                  ? "Cannot delete built-in plan"
                                  : "Delete"
                              }
                              disabled={isBuiltInPlan(plan.plan_type)}
                              style={{
                                color: isBuiltInPlan(plan.plan_type)
                                  ? "#d1d5db"
                                  : "#ef4444",
                                cursor: isBuiltInPlan(plan.plan_type)
                                  ? "not-allowed"
                                  : "pointer",
                              }}
                            >
                              <FiTrash2 size={18} />
                            </IconButton>
                          </ActionIcons>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </StyledTable>
          )}
          {saveError && (
            <div style={{ color: "red", marginTop: "10px" }}>{saveError}</div>
          )}
          {saveSuccess && (
            <div style={{ color: "#059669", marginTop: "10px" }}>
              {saveSuccess}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Add IconButton styled component
  const IconButton = styled.button`
    background: none;
    border: none;
    padding: 6px;
    border-radius: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    transition: background 0.15s;
    &:hover:not(:disabled) {
      background: #f3f4f6;
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `;

  const renderSettingsTab = () => (
    <>
      {/* Admin User Settings Section */}
      <SettingsSection>
        <SettingsGrid>
          <Card>
            <CardTitle>Profile Information</CardTitle>
            <CardContent>
              <ProfileSection />
            </CardContent>
          </Card>
          <Card>
            <CardTitle>Security & Account</CardTitle>
            <CardContent>
              <SecuritySection />
            </CardContent>
          </Card>
        </SettingsGrid>
        {/* Plan Limits Card */}
        <div style={{ marginTop: 32 }}>
          <PlanLimitsCard />
        </div>
      </SettingsSection>
      {/* Feature Toggles Section - Temporarily Commented Out
      <SettingsSection>
        <SettingsTitle>Feature Toggles</SettingsTitle>
        <ToggleContainer>
          <ToggleLabel>
            <ToggleTitle>Voice Messages</ToggleTitle>
            <ToggleDescription>Enable voice recording and transcription features</ToggleDescription>
          </ToggleLabel>
          <ToggleSwitch
            active={featureSettings.enableVoiceMessages}
            onClick={() => handleFeatureToggle('enableVoiceMessages')}
          />
        </ToggleContainer>
        <ToggleContainer>
          <ToggleLabel>
            <ToggleTitle>Image Uploads</ToggleTitle>
            <ToggleDescription>Enable image upload and analysis features</ToggleDescription>
          </ToggleLabel>
          <ToggleSwitch
            active={featureSettings.enableImageUploads}
            onClick={() => handleFeatureToggle('enableImageUploads')}
          />
        </ToggleContainer>
        <ToggleContainer>
          <ToggleLabel>
            <ToggleTitle>Text-to-Speech (TTS)</ToggleTitle>
            <ToggleDescription>Enable AI voice responses using ElevenLabs</ToggleDescription>
          </ToggleLabel>
          <ToggleSwitch
            active={featureSettings.enableTTS}
            onClick={() => handleFeatureToggle('enableTTS')}
          />
        </ToggleContainer>
        <ToggleContainer>
          <ToggleLabel>
            <ToggleTitle>Video Responses</ToggleTitle>
            <ToggleDescription>Enable video content in AI responses</ToggleDescription>
          </ToggleLabel>
          <ToggleSwitch
            active={featureSettings.enableVideoResponses}
            onClick={() => handleFeatureToggle('enableVideoResponses')}
          />
        </ToggleContainer>
        <SaveButton onClick={handleSaveSettings} disabled={saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </SaveButton>
      </SettingsSection>
      */}
    </>
  );

const Modal: React.FC<{
  children: React.ReactNode;
  onClose: () => void;
}> = ({ children, onClose }) => (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0,0,0,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 999,
    }}
  >
    <div
      style={{
        background: "#fff",
        padding: "20px",
        borderRadius: "8px",
        minWidth: "400px",
      }}
    >
      {children}
      <div style={{ marginTop: "1rem", textAlign: "right" }}>
        <button
          onClick={onClose}
          style={{
            background: "#ef4444",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            padding: "8px 14px",
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>
    </div>
  </div>
);


const AdminsTab: React.FC = () => {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [errorAdmins, setErrorAdmins] = useState<string | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

const [newAdmin, setNewAdmin] = useState({
  name: "",
  email: "",
  password: "",
  user_type: "admin", // default
});


  const [editAdmin, setEditAdmin] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    password: "",
  });

  const token =
    localStorage.getItem("auth_token") ||
    localStorage.getItem("firebase_token");

  // ✅ Fetch admins
  const fetchAdmins = async () => {
    setLoadingAdmins(true);
    setErrorAdmins(null);
    try {
      const res = await fetch(`${API_URL}/admin/admins`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success)
        throw new Error(data.message || "Failed to fetch admins");
      setAdmins(data.admins || []);
    } catch (err: any) {
      setErrorAdmins(err.message || "Error fetching admins");
    } finally {
      setLoadingAdmins(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  // ✅ Create admin
const handleCreateAdmin = async () => {
  if (!newAdmin.name || !newAdmin.email || !newAdmin.password) {
    alert("All fields are required");
    return;
  }

  try {
    const res = await fetch(`${API_URL}/admin/admins`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(newAdmin), // 🟩 includes user_type
    });
    const data = await res.json();

    if (!data.success)
      throw new Error(data.message || "Failed to create admin");

    setNewAdmin({ name: "", email: "", password: "", user_type: "admin" });
    setShowCreateModal(false);
    fetchAdmins();
  } catch (err: any) {
    alert(err.message || "Error creating admin");
  }
};


  // ✅ Update admin
  const handleUpdateAdmin = async () => {
    if (!editAdmin) return;
    try {
      const res = await fetch(`${API_URL}/admin/admins/${editAdmin.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (!data.success)
        throw new Error(data.message || "Failed to update admin");
      setEditAdmin(null);
      setShowEditModal(false);
      fetchAdmins();
    } catch (err: any) {
      alert(err.message || "Error updating admin");
    }
  };

  // ✅ Delete admin
  const handleDeleteAdmin = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this admin?"))
      return;
    try {
      const res = await fetch(`${API_URL}/admin/admins/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success)
        throw new Error(data.message || "Failed to delete admin");
      fetchAdmins();
    } catch (err: any) {
      alert(err.message || "Error deleting admin");
    }
  };

  // ✅ FIX #2: Use functional state updates to prevent stale closures
  const handleNewAdminChange = (field: keyof typeof newAdmin) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewAdmin((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleEditFormChange = (field: keyof typeof editForm) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <div>
      <h2 style={{ marginBottom: "1rem" }}>Admins Management</h2>

<table
  style={{
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0 8px",
    background: "transparent",
  }}
>
  <thead>
    <tr
      style={{
        background: "linear-gradient(90deg, #E89B3C, #FF9900)",
        color: "white",
        textAlign: "left",
      }}
    >
      <th
        style={{
          padding: "12px 16px",
          borderTopLeftRadius: "8px",
          fontWeight: 600,
        }}
      >
        Name
      </th>
      <th style={{ padding: "12px 16px", fontWeight: 600 }}>Email</th>
      <th style={{ padding: "12px 16px", fontWeight: 600 }}>Role</th>
      <th
        style={{
          padding: "12px 16px",
          borderTopRightRadius: "8px",
          fontWeight: 600,
        }}
      >
        Actions
      </th>
    </tr>
  </thead>

  <tbody>
    {admins.length > 0 ? (
      admins.map((admin, index) => (
        <tr
          key={admin.id}
          style={{
            background: index % 2 === 0 ? "#fff" : "#f9fafb",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
            borderRadius: "8px",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) =>
            ((e.currentTarget.style.boxShadow =
              "0 4px 12px rgba(0, 0, 0, 0.1)"),
            (e.currentTarget.style.transform = "scale(1.01)"))
          }
          onMouseLeave={(e) =>
            ((e.currentTarget.style.boxShadow =
              "0 2px 6px rgba(0, 0, 0, 0.05)"),
            (e.currentTarget.style.transform = "scale(1)"))
          }
        >
          <td style={{ padding: "12px 16px", borderRadius: "8px 0 0 8px" }}>
            <strong style={{ color: "#232B3A" }}>{admin.name}</strong>
          </td>
          <td style={{ padding: "12px 16px", color: "#555" }}>
            {admin.email}
          </td>
          <td style={{ padding: "12px 16px", color: "#6b7280" }}>
            {admin.user_type || "admin"}
          </td>
          <td
            style={{
              padding: "12px 16px",
              borderRadius: "0 8px 8px 0",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <button
                title="Edit"
                onClick={() => {
                  setEditAdmin(admin);
                  setEditForm({
                    name: admin.name,
                    email: admin.email,
                    password: "",
                  });
                  setShowEditModal(true);
                }}
                style={{
                  background: "#3B82F6",
                  border: "none",
                  color: "white",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  transition: "0.2s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#2563EB")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#3B82F6")
                }
              >
                <FiEdit2 size={16} /> Edit
              </button>

              <button
                title="Delete"
                onClick={() => handleDeleteAdmin(admin.id)}
                style={{
                  background: "#EF4444",
                  border: "none",
                  color: "white",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  transition: "0.2s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#DC2626")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#EF4444")
                }
              >
                <FiTrash2 size={16} /> Delete
              </button>
            </div>
          </td>
        </tr>
      ))
    ) : (
      <tr>
        <td
          colSpan={4}
          style={{
            padding: "20px",
            textAlign: "center",
            background: "#f3f4f6",
            borderRadius: "8px",
            color: "#6b7280",
          }}
        >
          No admins found
        </td>
      </tr>
    )}
  </tbody>
</table>


      <button
        onClick={() => setShowCreateModal(true)}
        style={{
          marginTop: "1rem",
          background: "linear-gradient(135deg, #E89B3C, #FF9900)",
          color: "#fff",
          border: "none",
          borderRadius: "8px",
          padding: "10px 18px",
          cursor: "pointer",
        }}
      >
        Create Admin
      </button>
{showCreateModal && (
  <Modal
    onClose={() => setShowCreateModal(false)}

  >
    <h3 style={{ marginBottom: "16px", fontSize: "20px", color: "#0A65CC" }}>
      Create New Admin
    </h3>

    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <input
        type="text"
        placeholder="Full Name"
        value={newAdmin.name}
        onChange={handleNewAdminChange("name")}
        style={{
          padding: "10px 12px",
          borderRadius: "8px",
          border: "1px solid #ccc",
        }}
      />

      <input
        type="email"
        placeholder="Email Address"
        value={newAdmin.email}
        onChange={handleNewAdminChange("email")}
        style={{
          padding: "10px 12px",
          borderRadius: "8px",
          border: "1px solid #ccc",
        }}
      />

      <input
        type="password"
        placeholder="Password"
        value={newAdmin.password}
        onChange={handleNewAdminChange("password")}
        style={{
          padding: "10px 12px",
          borderRadius: "8px",
          border: "1px solid #ccc",
        }}
      />

      {/* 🟩 Role Dropdown */}
      <select
        value={newAdmin.user_type}
        onChange={(e) =>
          setNewAdmin((prev) => ({ ...prev, user_type: e.target.value }))
        }
        style={{
          padding: "10px 12px",
          borderRadius: "8px",
          border: "1px solid #ccc",
          backgroundColor: "#fff",
          color: "#333",
          fontWeight: 500,
        }}
      >
        <option value="admin">Admin</option>
        <option value="super-admin">Super Admin</option>
      </select>

      <button
        onClick={handleCreateAdmin}
        style={{
          marginTop: "16px",
          background: "linear-gradient(135deg, #E89B3C, #FF9900)",
          color: "#fff",
          border: "none",
          borderRadius: "8px",
          padding: "12px 18px",
          cursor: "pointer",
          fontWeight: 600,
        }}
      >
        Save Admin
      </button>
    </div>
  </Modal>
)}


      {/* ✅ Edit Modal */}
      {showEditModal && editAdmin && (
        <Modal onClose={() => setShowEditModal(false)}>
          <h3>Edit Admin</h3>
          <input
            type="text"
            placeholder="Name"
            value={editForm.name}
            onChange={handleEditFormChange("name")}
            style={{
              marginBottom: "8px",
              display: "block",
              width: "100%",
              padding: "8px",
              boxSizing: "border-box",
            }}
          />
          <input
            type="email"
            placeholder="Email"
            value={editForm.email}
            onChange={handleEditFormChange("email")}
            style={{
              marginBottom: "8px",
              display: "block",
              width: "100%",
              padding: "8px",
              boxSizing: "border-box",
            }}
          />
          <input
            type="password"
            placeholder="New Password (leave empty to keep current)"
            value={editForm.password}
            onChange={handleEditFormChange("password")}
            style={{
              marginBottom: "8px",
              display: "block",
              width: "100%",
              padding: "8px",
              boxSizing: "border-box",
            }}
          />
          <button
            onClick={handleUpdateAdmin}
            style={{
              background: "linear-gradient(135deg, #10b981, #059669)",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "10px 18px",
              cursor: "pointer",
            }}
          >
            Save Changes
          </button>
        </Modal>
      )}
    </div>
  );
};


const KnowledgeTab: React.FC = () => {
  const API_BASE = "https://unitive-overspacious-emilio.ngrok-free.dev/api/v1";
  const Node_url = process.env.REACT_APP_API_URL;
  console.log(Node_url);

  const adminToken =
    localStorage.getItem("auth_token") ||
    localStorage.getItem("firebase_token") ||
    "";

  const authHeader = useMemo(() => {
    return adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
  }, [adminToken]) as Record<string, string>;

  // UI State
  const [showDocModal, setShowDocModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Docs state
  const [docFiles, setDocFiles] = useState<any[]>([]);
  const [pickedDocFiles, setPickedDocFiles] = useState<File[]>([]);
  const [uploadingDocs, setUploadingDocs] = useState(false);

  // Videos state (✅ now supports multiple)
  const [videos, setVideos] = useState<any[]>([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoTitle, setVideoTitle] = useState("");
  const [videoCategory, setVideoCategory] = useState("");
  const [videoTags, setVideoTags] = useState("");
  const [pickedVideoFiles, setPickedVideoFiles] = useState<File[]>([]);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  // Common fields
  const [author, setAuthor] = useState("");
  const [category, setCategory] = useState("training_guide");
  const [customCategory, setCustomCategory] = useState("");
  const [activeTable, setActiveTable] = useState<"documents" | "videos">(
    "documents"
  );

  // Search
  const [docSearch, setDocSearch] = useState("");
  const [videoSearch, setVideoSearch] = useState("");

  // Unanswered state
  const [unanswered, setUnanswered] = useState<any[]>([]);
  const [loadingUnanswered, setLoadingUnanswered] = useState(false);

  // Loader states
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [loadingVideos, setLoadingVideos] = useState(false);

  // File refs
  const docInputRef = useRef<HTMLInputElement | null>(null);

  // Delete confirmation modal
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "document" | "video";
    id: string | number;
  } | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  // Fetch unanswered queries
  const fetchUnanswered = async () => {
    setLoadingUnanswered(true);
    try {
      const res = await fetch(`${Node_url}/queries`, {
        headers: {
          Accept: "application/json",
          ...authHeader,
        },
      });
      if (!res.ok) throw new Error("Failed to fetch unanswered queries");
      const data = await res.json();
      setUnanswered(data || []);
    } catch (e: any) {
      console.error("❌ Failed to fetch unanswered:", e.message);
      toast.error("Error fetching unanswered queries");
    } finally {
      setLoadingUnanswered(false);
    }
  };

  // Delete query (mark solved)
  const solveQuery = async (id: number) => {
    try {
      const res = await fetch(`${Node_url}/queries/${id}`, {
        method: "DELETE",
        headers: { Accept: "application/json", ...authHeader },
      });
      if (!res.ok) throw new Error("Failed to solve query");

      setUnanswered((prev) => prev.filter((q) => q.id !== id));
      toast.success("Query marked as solved ✅");
    } catch (e: any) {
      console.error("❌ Solve failed:", e.message);
      toast.error("Failed to solve query");
    }
  };

  // Helpers
  const normalizeStats = (data: any) => {
    const candidates =
      data?.documents ||
      data?.items ||
      data?.files ||
      data?.stats?.documents ||
      [];
    if (!Array.isArray(candidates)) return [];

    return candidates.map((it: any, idx: number) => ({
      id: it.id ?? it.document_id ?? it._id ?? idx,
      file_name: it.file_name ?? it.filename ?? it.name ?? "unnamed",
      category: it.category ?? it.tags?.category ?? "",
      author: it.author ?? it.uploader ?? "",
      created_at: it.created_at ?? it.createdAt ?? it.ingested_at ?? "",
      file_url: it.file_url ?? it.url ?? "",
    }));
  };

  const normalizeVideos = (data: any[]) => {
    if (!Array.isArray(data)) return [];
    return data.map((it: any, idx: number) => ({
      id: it.video_id ?? it.id ?? idx,
      title: it.title ?? it.name ?? "Untitled",
      category: it.category ?? "-",
      video_url: it.video_url ?? it.url ?? "",
      uploader: it.uploader ?? it.author ?? "unknown",
      processed_at: it.processed_at ?? it.created_at ?? null,
    }));
  };

  // API Calls (Docs)
  const fetchIngestStats = async () => {
    setLoadingDocs(true);
    try {
      const res = await axios.get(`${API_BASE}/ingest/documents`, {
        headers: {
          Accept: "application/json",
          ...authHeader,
          "ngrok-skip-browser-warning": "true",
        },
        validateStatus: () => true,
      });

      const contentType = res.headers["content-type"] || "";
      if (contentType.includes("application/json")) {
        setDocFiles(normalizeStats(res.data));
      } else {
        console.error("❌ Expected JSON, got:", contentType);
        toast.error("Failed to load documents");
        setDocFiles([]);
      }
    } catch (e: any) {
      console.error("🚨 Fetch failed:", e.message);
      toast.error("Error fetching documents");
      setDocFiles([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const uploadDocuments = async () => {
    if (!pickedDocFiles.length) return toast.error("Please choose a file");
    if (!author.trim()) return toast.error("Author is required");

    const finalCategory =
      category === "custom" ? customCategory.trim() : category;
    if (!finalCategory) return toast.error("Category is required");

    setUploadingDocs(true);
    try {
      const fd = new FormData();
      pickedDocFiles.forEach((f) => fd.append("files", f));
      fd.append("category", finalCategory);
      fd.append("author", author.trim());

      const res = await fetch(`${API_BASE}/ingest/files`, {
        method: "POST",
        headers: { ...authHeader },
        body: fd,
      });

      const json = await res.json();
      setDocFiles((prev) => [...prev, ...normalizeStats(json)]);
      toast.success("Document(s) uploaded");

      setPickedDocFiles([]);
      setAuthor("");
      setCategory("training_guide");
      setCustomCategory("");
      setShowDocModal(false);
      await fetchIngestStats();
    } catch (e: any) {
      console.error("Upload failed", e);
      toast.error("Upload failed");
    } finally {
      setUploadingDocs(false);
    }
  };

const confirmDelete = async (type: "document" | "video", id: string | number) => {
  try {
    if (type === "document") {
      await fetch(`${API_BASE}/ingest/documents/${id}`, {
        method: "DELETE",
        headers: { Accept: "application/json", ...authHeader },
      });
      setDocFiles((prev) => prev.filter((d) => d.id !== id));
      toast.success("Document deleted");
    } else {
      const res = await fetch(`${API_BASE}/videos/delete/${id}`, {
        method: "DELETE",
        headers: { ...authHeader },
      });
      if (!res.ok) throw new Error("Failed to delete video");
      setVideos((prev) => prev.filter((v) => v.id !== id));
      toast.success("Video deleted");
    }
  } catch (e: any) {
    console.error("Delete failed", e);
    toast.error("Delete failed");
  }
};


  // API Calls (Videos)
  const fetchVideos = async () => {
    setLoadingVideos(true);
    try {
      const res = await fetch(`${API_BASE}/videos/list`, {
        headers: {
          Accept: "application/json",
          ...authHeader,
          "ngrok-skip-browser-warning": "true",
        },
      });

      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const json = await res.json();
        setVideos(Array.isArray(json.videos) ? normalizeVideos(json.videos) : []);
      } else {
        toast.error("Failed to load videos");
        setVideos([]);
      }
    } catch (e: any) {
      console.error("🚨 Failed to fetch videos:", e.message);
      toast.error("Error fetching videos");
      setVideos([]);
    } finally {
      setLoadingVideos(false);
    }
  };

  // ✅ MULTIPLE VIDEO UPLOAD SUPPORT
const processVideo = async () => {
  if (!videoUrl && pickedVideoFiles.length === 0)
    return toast.error("Provide at least one video file or a URL");
  if (!videoTitle.trim()) return toast.error("Title required");

  setUploadingVideo(true);
  try {
    // 🟧 CASE 1: Local files (multiple)
    if (pickedVideoFiles.length > 0) {
      const fd = new FormData();
      pickedVideoFiles.forEach((file) => fd.append("video_files", file));
      fd.append("title", videoTitle);
      fd.append("category", videoCategory);
      if (videoTags) fd.append("tags", videoTags);

      const res = await fetch(`${API_BASE}/videos/process`, {
        method: "POST",
        headers: { ...authHeader },
        body: fd,
      });

      const json = await res.json();

      // ✅ Support both single-object or batch result responses
      if (Array.isArray(json.results)) {
        setVideos((prev) => [...prev, ...normalizeVideos(json.results)]);
        toast.success(`${json.successful} video(s) processed`);
      } else if (json.video_id) {
        setVideos((prev) => [...prev, ...normalizeVideos([json])]);
        toast.success("Video processed successfully");
      } else {
        toast.error("Unexpected response from server");
      }
    }

    // 🟧 CASE 2: Video URL
    else if (videoUrl) {
      const body = {
        video_url: videoUrl,
        title: videoTitle,
        category: videoCategory,
        tags: videoTags ? videoTags.split(",").map((t) => t.trim()) : [],
      };
      const res = await fetch(`${API_BASE}/videos/process`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeader,
        },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      setVideos((prev) => [...prev, ...normalizeVideos([json])]);
      toast.success("Video processed from URL");
    }

    // 🧹 Reset state
    setVideoUrl("");
    setVideoTitle("");
    setVideoCategory("");
    setVideoTags("");
    setPickedVideoFiles([]);
    setShowVideoModal(false);

    // ♻️ Optionally refetch the list after upload
    await fetchVideos();
  } catch (e: any) {
    console.error("Process video failed", e);
    toast.error("Processing failed");
  } finally {
    setUploadingVideo(false);
  }
};


  // Effects
  useEffect(() => {
    fetchIngestStats();
    fetchVideos();
    fetchUnanswered();
  }, []);

  // Filters & Pagination (same as before)
  const filteredDocs = useMemo(() => {
    const q = docSearch.toLowerCase();
    return docFiles.filter(
      (f) =>
        f.file_name.toLowerCase().includes(q) ||
        (f.category || "").toLowerCase().includes(q) ||
        (f.author || "").toLowerCase().includes(q)
    );
  }, [docFiles, docSearch]);

  const filteredVideos = useMemo(() => {
    const q = videoSearch.toLowerCase();
    return videos.filter(
      (f) =>
        f.title.toLowerCase().includes(q) ||
        (f.category || "").toLowerCase().includes(q)
    );
  }, [videos, videoSearch]);

  const paginatedDocs = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredDocs.slice(start, start + pageSize);
  }, [filteredDocs, currentPage]);

  const paginatedVideos = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredVideos.slice(start, start + pageSize);
  }, [filteredVideos, currentPage]);

  const totalPages =
    activeTable === "documents"
      ? Math.ceil(filteredDocs.length / pageSize)
      : Math.ceil(filteredVideos.length / pageSize);

  return (
    <div className="p-6 space-y-8">
      <Toaster position="top-right" />

      {/* Upload Buttons */}
      <div className="flex gap-8">
        <div className="flex flex-col items-center flex-1">
          <button
            onClick={() => setShowDocModal(true)}
            className="w-full bg-gradient-to-br from-[#E89B3C] to-[#FF9900] text-white font-semibold px-6 py-4 rounded-xl shadow-md hover:opacity-90"
          >
            Upload Documents
          </button>
          <span className="mt-2 text-sm text-gray-500">
            Allowed: .pdf, .doc, .docx, .txt, .md, .csv
          </span>
        </div>

        <div className="flex flex-col items-center flex-1">
          <button
            onClick={() => setShowVideoModal(true)}
            className="w-full bg-gradient-to-br from-[#E89B3C] to-[#FF9900] text-white font-semibold px-6 py-4 rounded-xl shadow-md hover:opacity-90"
          >
            Upload Video/Audio
          </button>
          <span className="mt-2 text-sm text-gray-500">
            Allowed: mp4, mp3, or URL
          </span>
        </div>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search documents or videos..."
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A65CC]"
        value={activeTable === "documents" ? docSearch : videoSearch}
        onChange={(e) =>
          activeTable === "documents"
            ? setDocSearch(e.target.value)
            : setVideoSearch(e.target.value)
        }
      />

      {/* Toggle Table */}
      <div className="flex gap-4 mb-4">
        <button
          onClick={() => setActiveTable("documents")}
          className={`flex-1 px-4 py-2 rounded-md font-semibold ${
            activeTable === "documents"
              ? "bg-gray-300 text-gray-900"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          Documents
        </button>
        <button
          onClick={() => setActiveTable("videos")}
          className={`flex-1 px-4 py-2 rounded-md font-semibold ${
            activeTable === "videos"
              ? "bg-gray-300 text-gray-900"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          Videos
        </button>
      </div>

      {/* Shared Table */}
      <div>
        {activeTable === "documents" ? (
          loadingDocs ? (
            <p className="text-gray-500 text-sm text-center py-6">
              Loading documents...
            </p>
          ) : filteredDocs.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-6">
              No documents uploaded yet.
            </p>
          ) : (
<div className="overflow-hidden border border-gray-200 rounded-xl shadow-sm">
  <table className="w-full text-sm text-left">
    <thead className="bg-gray-50 text-gray-700 uppercase text-xs">
      <tr>
        <th className="px-6 py-3">File Name</th>
        <th className="px-6 py-3">Category</th>
        <th className="px-6 py-3">Author</th>
        <th className="px-6 py-3 text-center">Link</th>
        <th className="px-6 py-3 text-center">Actions</th>
      </tr>
    </thead>

    <tbody className="divide-y divide-gray-100">
      {paginatedDocs.map((d) => (
        <tr
          key={d.id}
          className="hover:bg-gray-50 transition-colors duration-200"
        >
          {/* 📄 File name */}
          <td className="px-6 py-4 font-medium text-gray-800">
            {d.file_name}
          </td>

          {/* 📂 Category */}
          <td className="px-6 py-4 text-gray-600">{d.category || "-"}</td>

          {/* ✍️ Author */}
          <td className="px-6 py-4 text-gray-600">{d.author || "-"}</td>

          {/* 🔗 Link (Preview / Download) */}
          <td className="px-6 py-4 text-center">
            {d.file_url || d.url ? (
              <div className="flex justify-center gap-3">
                {/* 👁️ Preview link */}
                <a
                  href={d.file_url || d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-600 hover:text-blue-800 transition"
                  title="Preview Document"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </a>

                {/* 💾 Download link */}
                <a
                  href={d.file_url || d.url}
                  download
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-50 hover:bg-green-100 text-green-600 hover:text-green-800 transition"
                  title="Download Document"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                </a>
              </div>
            ) : (
              <span className="text-gray-400 text-xs">No link</span>
            )}
          </td>

          {/* 🗑️ Actions */}
          <td className="px-6 py-4 text-center">
            <button
              onClick={() => confirmDelete("document", d.id)}

              className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-50 hover:bg-red-100 text-red-500 hover:text-red-700 transition"
              title="Delete Document"
            >
              <FiTrash2 size={16} />
            </button>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>


          )
        ) : loadingVideos ? (
          <p className="text-gray-500 text-sm text-center py-6">
            Loading videos...
          </p>
        ) : filteredVideos.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-6">
            No videos processed yet.
          </p>
        ) : (
          <div className="overflow-hidden border border-gray-200 rounded-xl shadow-sm">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-700 uppercase text-xs">
                <tr>
                  <th className="px-6 py-3">Title</th>
                  <th className="px-6 py-3">Category</th>
                 <th className="px-6 py-3">link</th>
                  <th className="px-6 py-3">Uploader</th>
                  <th className="px-6 py-3">Processed</th>
                  <th className="px-6 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedVideos.map((v) => (
                  <tr
                    key={v.id}
                    className="hover:bg-gray-50 transition-colors duration-200"
                  >
                    <td className="px-6 py-4 font-medium text-gray-800">
                      {v.title}
                    </td>
                    <td className="px-6 py-4 text-gray-600">{v.category}</td>
                   <td className="px-6 py-4 text-gray-600">
  {v.video_url ? (
    <a
      href={v.video_url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:underline"
    >
      Preview
    </a>
  ) : (
    "-"
  )}
</td>

                    <td className="px-6 py-4 text-gray-600">{v.uploader}</td>
                    <td className="px-6 py-4 text-gray-500">
                      {v.processed_at
                        ? new Date(v.processed_at).toLocaleString()
                        : "-"}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => confirmDelete("video", v.id)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-50 hover:bg-red-100 text-red-500 hover:text-red-700 transition"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded-md border bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded-md border bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Unanswered */}
      <div className="mt-10">
        <h3 className="text-lg font-semibold text-[#0A65CC] mb-4">
          Unanswered Questions
        </h3>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          {loadingUnanswered ? (
            <p className="text-gray-500 text-sm text-center py-4">Loading...</p>
          ) : unanswered.length === 0 ? (
            <p className="text-gray-500 text-sm text-center">
              No unanswered questions 🎉
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {unanswered.map((q) => (
                <li
                  key={q.id}
                  className="flex justify-between items-center p-3 hover:bg-gray-50 transition rounded-md"
                >
                  <span className="text-sm text-gray-700">
                    {q.message || q.question}
                  </span>
                  <button
                    onClick={() => solveQuery(q.id)}
                    className="px-3 py-1 text-sm font-medium text-white bg-green-500 hover:bg-green-600 rounded-lg shadow-sm transition"
                  >
                    Solve
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Document Modal */}
      {showDocModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl w-full max-w-md relative">
            <button
              className="absolute top-3 right-3 text-gray-500"
              onClick={() => setShowDocModal(false)}
            >
              <FiX size={20} />
            </button>
            <h3 className="text-lg font-semibold mb-4 text-[#0A65CC]">
              Upload Documents
            </h3>
            <input
              type="text"
              placeholder="Author"
              className="w-full mb-3 p-2 border rounded-lg"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
            />
            <input
              ref={docInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.txt,.md,.csv"
              multiple
              className="mb-3 text-sm"
              onChange={(e) =>
                setPickedDocFiles(
                  e.target.files ? Array.from(e.target.files) : []
                )
              }
            />
            <button
              onClick={uploadDocuments}
              disabled={uploadingDocs}
              className="w-full bg-gradient-to-br from-[#E89B3C] to-[#FF9900] text-white py-2 rounded-lg"
            >
              {uploadingDocs ? "Uploading…" : "Upload"}
            </button>
          </div>
        </div>
      )}

   {showVideoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl w-full max-w-md relative">
            <button
              className="absolute top-3 right-3 text-gray-500"
              onClick={() => setShowVideoModal(false)}
            >
              <FiX size={20} />
            </button>
            <h3 className="text-lg font-semibold mb-4 text-[#0A65CC]">
              Process Video / Audio
            </h3>

            <input
              type="text"
              placeholder="Title"
              className="w-full mb-3 p-2 border rounded-lg"
              value={videoTitle}
              onChange={(e) => setVideoTitle(e.target.value)}
            />
            <input
              type="text"
              placeholder="Category"
              className="w-full mb-3 p-2 border rounded-lg"
              value={videoCategory}
              onChange={(e) => setVideoCategory(e.target.value)}
            />
            <input
              type="text"
              placeholder="Tags (comma separated)"
              className="w-full mb-3 p-2 border rounded-lg"
              value={videoTags}
              onChange={(e) => setVideoTags(e.target.value)}
            />
            <input
              type="url"
              placeholder="Video URL"
              className="w-full mb-3 p-2 border rounded-lg"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
            />

            {/* ✅ Multiple File Upload */}
            <input
              type="file"
              accept="video/*,audio/*"
              multiple
              className="mb-3 text-sm"
              onChange={(e) =>
                setPickedVideoFiles(e.target.files ? Array.from(e.target.files) : [])
              }
            />

            {pickedVideoFiles.length > 0 && (
              <ul className="mb-3 text-sm text-gray-600">
                {pickedVideoFiles.map((f, i) => (
                  <li key={i}>• {f.name}</li>
                ))}
              </ul>
            )}

            <button
              onClick={processVideo}
              disabled={uploadingVideo}
              className="w-full bg-gradient-to-br from-[#E89B3C] to-[#FF9900] text-white py-2 rounded-lg"
            >
              {uploadingVideo ? "Processing…" : "Process"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


const LibraryTab: React.FC = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [source, setSource] = useState("vimeo");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const API_BASE = "https://unitive-overspacious-emilio.ngrok-free.dev/api/v1";

  // 🔄 Step 1: Sync videos from Vimeo or Google Drive
  const syncVideos = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_BASE}/library/sync?source=${source}`, {
        method: "POST",
      });
      const data = await res.json();
      alert(`✅ Sync complete!\n${data.message || ""}`);
      fetchVideos();
    } catch (err) {
      alert("❌ Failed to sync videos");
    } finally {
      setSyncing(false);
    }
  };

  // 📹 Step 2: Fetch videos for listing
  const fetchVideos = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/library/videos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          page,
          page_size: pageSize,
          source,
          status_filter: "not_in_knowledge_base",
        }),
      });
      const data = await res.json();
      setVideos(data.videos || []);
      setTotal(data.total || 0);
    } catch (err) {
      alert("❌ Failed to load videos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, [source, page]);

  // ✅ Select or deselect
  const toggleSelect = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  // ✅ Select all
  const toggleSelectAll = () => {
    if (selected.length === videos.length) {
      setSelected([]);
    } else {
      setSelected(videos.map((v) => v.video_id));
    }
  };

  // 🚀 Step 3: Process selected or all
  const processVideos = async (ids: string[]) => {
    if (!ids.length) return alert("Select at least one video");
    if (ids.length > 50) return alert("Max 50 videos per request");

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/library/process`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          video_ids: ids,
          category: "training",
          tags: ["dog", "training"],
        }),
      });
      const data = await res.json();
      alert(` ${data.message}`);
      setSelected([]);
      fetchVideos();
    } catch (err: any) {
      alert("❌ Failed to process videos: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 w-full">
      {/* 🔘 Controls Row */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
        <div className="flex gap-3 items-center">
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="vimeo">Vimeo</option>
            <option value="google_drive">Google Drive</option>
          </select>

          <button
            onClick={syncVideos}
            disabled={syncing}
            className={`px-4 py-2 rounded-md text-white ${
              syncing ? "bg-gray-400" : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {syncing ? "Syncing..." : `Sync ${source}`}
          </button>

          <button
            onClick={() => processVideos(selected)}
            disabled={loading || selected.length === 0}
            className={`px-4 py-2 rounded-md text-white ${
              selected.length === 0
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loading ? "Processing..." : "Process Selected"}
          </button>

          <button
            onClick={() => processVideos(videos.map((v) => v.video_id))}
            disabled={loading || videos.length === 0}
            className="px-4 py-2 rounded-md border border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            Process All
          </button>
        </div>

        {/* Pagination */}
        <div>
          <span className="text-sm text-gray-500">
            Page {page} of {Math.ceil(total / pageSize) || 1}
          </span>
          <button
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            className="ml-3 text-sm px-3 py-1 border rounded disabled:opacity-50"
            disabled={page === 1}
          >
            Prev
          </button>
          <button
            onClick={() => setPage((p) => p + 1)}
            className="ml-2 text-sm px-3 py-1 border rounded disabled:opacity-50"
            disabled={videos.length < pageSize}
          >
            Next
          </button>
        </div>
      </div>

      {/* 📋 Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="px-4 py-2">
                <input
                  type="checkbox"
                  checked={selected.length === videos.length && videos.length > 0}
                  onChange={toggleSelectAll}
                />
              </th>
              <th className="px-4 py-2">Title</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="text-center py-6">
                  Loading...
                </td>
              </tr>
            ) : videos.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center py-6 text-gray-500">
                  No videos found
                </td>
              </tr>
            ) : (
              videos.map((video) => (
                <tr
                  key={video.video_id}
                  className="border-t hover:bg-gray-50 transition"
                >
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selected.includes(video.video_id)}
                      onChange={() => toggleSelect(video.video_id)}
                    />
                  </td>
                  <td className="px-4 py-2 font-medium">{video.title}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        video.status === "completed"
                          ? "bg-green-100 text-green-700"
                          : video.status === "failed"
                          ? "bg-red-100 text-red-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {video.status}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => processVideos([video.video_id])}
                      className="text-blue-600 hover:underline"
                    >
                      Process
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};





  /* ---------- Main Return ---------- */
  return (
    <AdminContainer>
      <IconActionButton
          onClick={() => navigate('/')}
          title="Back to Home"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: '#FF9900',
            color: '#fff',
            border: 'none',
            padding: '0.6rem 1.3rem',
            borderRadius: '10px',
            fontWeight: 600,
            fontSize: '0.95rem',
            width: '150px', // ⬅️ Increase width here
            justifyContent: 'center',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#FFD700';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#FF9900';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <FiHome size={18} />
          Back to Home
        </IconActionButton>
      <AdminContent>
        <AccentBar />
        <Header>
          <HeaderRow>
            <HeaderMain>
              <UserAvatar>
                {adminUser && adminUser.avatar_url ? (
                  <img
                    src={adminUser.avatar_url}
                    alt="Profile"
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <FaPaw className="text-orange" size={34} />
                )}
              </UserAvatar>
              <AdminDetails>
                <AdminName>Welcome, {adminUser?.name || "Admin"}!</AdminName>
                <AdminEmail>{adminUser?.email}</AdminEmail>
              </AdminDetails>
            </HeaderMain>
            <HeaderActions>

                <IconActionButton onClick={() => navigate('/')} title="Back to Home">
    <FiHome />
  </IconActionButton>
              <IconActionButton
                onClick={() => setActiveTab("settings")}
                title="Settings"
              >
                <FiSettings />
              </IconActionButton>
              <IconActionButton onClick={signOut} title="Logout">
                <FiLogOut />
              </IconActionButton>
            </HeaderActions>
          </HeaderRow>
        </Header>

        <MainContent>
          <TabContainer>
            <Tab
              active={activeTab === "users"}
              onClick={() => setActiveTab("users")}
            >
              Users
            </Tab>
            <Tab
              active={activeTab === "analytics"}
              onClick={() => setActiveTab("analytics")}
            >
              Analytics
            </Tab>
            <Tab
              active={activeTab === "settings"}
              onClick={() => setActiveTab("settings")}
            >
              Settings
            </Tab>

{adminUser?.user_type === "super-admin" && (
  <>
    <Tab
      active={activeTab === "admins"}
      onClick={() => setActiveTab("admins")}
    >
      Admins
    </Tab>
        <Tab
      active={activeTab === "library"}
      onClick={() => setActiveTab("library")}
    >
      AI Library
    </Tab>
    <Tab
      active={activeTab === "knowledge"}
      onClick={() => setActiveTab("knowledge")}
    >
      Knowledge Base
    </Tab>

  </>
)}

          </TabContainer>

          {activeTab === "users" && renderUsersTab()}
          {activeTab === "analytics" && renderAnalyticsTab()}
          {activeTab === "settings" && renderSettingsTab()}
          {adminUser?.user_type === "super-admin" && activeTab === "admins" && (
            <AdminsTab />
          )}
                      {adminUser?.user_type === "super-admin" &&
  activeTab === "library" && <LibraryTab />}

          {adminUser?.user_type === "super-admin" &&
            activeTab === "knowledge" && <KnowledgeTab />}



        </MainContent>
      </AdminContent>

      {showUserModal && (
        <UserManagementModal
          user={selectedUser}
          onClose={handleCloseUserModal}
          onSave={handleUserUpdate} // 👈 Fix here
          loading={userModalLoading}
        />
      )}
    </AdminContainer>
  );
};

export default AdminDashboard;
